import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WineBarIcon from '@mui/icons-material/WineBar';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
  Box,
  CircularProgress,
  InputBase,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  useAccountPerformance,
  type AccountMomentum,
  type AccountPerformanceItem,
  type DateRange,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { fmt6PkNumber, toSixPacks } from '../lib/volume';
import { colors } from '../theme';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num as toNum,
  premisesLabel,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { PremisesBadge } from './PremisesBadge';
import { PREMISES_STYLE } from './premisesStyle';
import { useSectionExport } from '../hooks/useSectionExport';
import { SectionExplainer } from './SectionExplainer';
import { PremiumPagination } from './depletions/PremiumPagination';

interface TopAccountsTableProps {
  range?: DateRange;
  /** How many accounts to fetch from the server. Default 5000
   *  effectively loads every account in the dataset; the UI then
   *  paginates client-side so the rep can find any account by name
   *  or filter, not just the top-by-volume slice. */
  limit?: number;
}

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

// ----------------------------------------------------------------
// Shared primitives — same shapes as Product / State Performance.
// Kept in-file for now; can be extracted to a shared module when
// a fourth section needs them.
// ----------------------------------------------------------------

interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 320 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

interface TooltipBlockProps {
  title?: string;
  items?: Array<{ label: string; description: string }>;
  bullets?: string[];
  tip?: string;
  maxWidth?: number;
}

function TooltipBlock({ title, items, bullets, tip, maxWidth = 280 }: TooltipBlockProps) {
  return (
    <Box sx={{ p: 0.25, maxWidth }}>
      {title && (
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: colors.textPrimary, mb: 0.75 }}>
          {title}
        </Typography>
      )}
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.6,
            mb: bullets || tip ? 0.75 : 0,
          }}
        >
          {items.map((it) => (
            <Box key={it.label}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                }}
              >
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                {it.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bullets && bullets.length > 0 && (
        <Box
          component="ul"
          sx={{
            pl: 0,
            m: 0,
            mb: tip ? 0.75 : 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
          }}
        >
          {bullets.map((b, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 11,
                color: colors.textSecondary,
                lineHeight: 1.4,
                position: 'relative',
                pl: 1.25,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: colors.textMuted,
                },
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      )}
      {tip && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderTop: `1px solid ${colors.borderLight}`,
            pt: 0.6,
            mt: items || bullets ? 0.5 : 0,
          }}
        >
          {tip}
        </Typography>
      )}
    </Box>
  );
}

interface ColumnHeaderProps {
  children: ReactNode;
  tooltip: ReactNode;
  align?: 'left' | 'right';
}

function ColumnHeader({ children, tooltip, align = 'left' }: ColumnHeaderProps) {
  return (
    <Box sx={{ textAlign: align }}>
      <Tooltip title={tooltip}>
        <Box
          component="span"
          sx={{
            borderBottom: `1px dotted ${colors.textMuted}80`,
            cursor: 'help',
            paddingBottom: '1px',
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </Box>
  );
}

const MOMENTUM_PRESENTATION: Record<
  AccountMomentum,
  { label: string; color: string; bg: string; Icon: typeof TrendingUpIcon }
> = {
  rising: {
    label: 'Rising',
    color: colors.success,
    bg: `${colors.success}14`,
    Icon: TrendingUpIcon,
  },
  steady: { label: 'Steady', color: colors.textSecondary, bg: '#f1f5f9', Icon: TrendingFlatIcon },
  slipping: {
    label: 'Slipping',
    color: colors.warning,
    bg: `${colors.warning}14`,
    Icon: TrendingDownIcon,
  },
  declining: {
    label: 'Declining',
    color: colors.error,
    bg: `${colors.error}14`,
    Icon: TrendingDownIcon,
  },
  new: {
    label: 'New',
    color: colors.chartPrimary,
    bg: `${colors.chartPrimary}14`,
    Icon: TrendingUpIcon,
  },
};

function shortMonth(iso: string): string {
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[idx] ?? '?'} '${(y ?? '').slice(-2)}`;
}

function shortSkuName(full: string): string {
  for (const prefix of ['Hooten Young ', 'Hooten & Young ', 'Hooten and Young ']) {
    if (full.startsWith(prefix)) return full.slice(prefix.length).trim();
  }
  return full;
}

// ---- 12-month bar sparkline ----

interface SparklineProps {
  series: { period: string; cases_9l: string }[];
  color?: string;
}
const BAR_W = 9;
const BAR_GAP = 3;
const BAR_H = 30;
const ZERO_RAIL_H = 2;

function Sparkline({ series, color = colors.chartPrimary }: SparklineProps) {
  const values = series.map((p) => Number(p.cases_9l));
  const max = Math.max(...values, 0);
  const peakIdx = max > 0 ? values.indexOf(max) : -1;
  const lastIdx = series.length - 1;
  const firstLabel = series.length > 0 ? shortMonth(series[0]!.period) : '';
  const lastLabel = series.length > 0 ? shortMonth(series[series.length - 1]!.period) : '';
  const stripW = values.length * BAR_W + (values.length - 1) * BAR_GAP;

  return (
    <Box sx={{ display: 'inline-block' }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          gap: `${BAR_GAP}px`,
          height: BAR_H,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: colors.borderLight,
            zIndex: 0,
          },
        }}
      >
        {values.map((v, i) => {
          const isZero = v === 0;
          const h = max > 0 && v > 0 ? Math.max((v / max) * BAR_H, 4) : ZERO_RAIL_H;
          const isPeak = !isZero && i === peakIdx;
          const isLast = i === lastIdx;
          const period = series[i]?.period ?? '';
          return (
            <Tooltip
              key={period}
              title={
                <Box>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                    {shortMonth(period)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {isZero ? 'No depletions' : `${fmt6PkNumber(v)} 6-pk`}
                  </Typography>
                  {isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.success, fontWeight: 700 }}>
                      Peak month
                    </Typography>
                  )}
                  {isLast && !isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.chartPrimary, fontWeight: 700 }}>
                      Latest month
                    </Typography>
                  )}
                </Box>
              }
            >
              <Box
                sx={{
                  position: 'relative',
                  width: BAR_W,
                  height: h,
                  bgcolor: isZero ? colors.borderLight : color,
                  opacity: isZero ? 1 : isPeak ? 0.95 : 0.45,
                  borderRadius: '2px 2px 0 0',
                  cursor: 'pointer',
                  zIndex: 1,
                  outline: isLast && !isZero ? `1.5px solid ${color}` : 'none',
                  outlineOffset: '-1px',
                  transition: 'opacity 0.15s, transform 0.15s',
                  '&:hover': {
                    opacity: isZero ? 1 : 0.95,
                    transform: isZero ? 'none' : 'scaleY(1.08)',
                    transformOrigin: 'bottom',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box sx={{ mt: 0.4, display: 'flex', justifyContent: 'space-between', width: stripW }}>
        <Typography sx={{ fontSize: 9, color: colors.textMuted, lineHeight: 1, fontWeight: 500 }}>
          {firstLabel}
        </Typography>
        <Typography sx={{ fontSize: 9, color: colors.textMuted, lineHeight: 1, fontWeight: 500 }}>
          {lastLabel}
        </Typography>
      </Box>
    </Box>
  );
}

// ---- QoQ / YoY chips ----

const LOW_BASE_THRESHOLD_9L = 5;

function qoqChipText(velocityPct: number | null, prior3m: number, recent3m: number): string {
  if (velocityPct === null) return 'New';
  if (prior3m < LOW_BASE_THRESHOLD_9L) {
    const diff = recent3m - prior3m;
    return `${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  }
  if (velocityPct > 999) return '+999%+';
  if (velocityPct < -999) return '−999%+';
  return `${velocityPct > 0 ? '+' : ''}${velocityPct.toFixed(0)}%`;
}

interface MomentumChipProps {
  momentum: AccountMomentum;
  velocityPct: number | null;
  recent3m: number;
  prior3m: number;
  compact?: boolean;
}

function MomentumChip({
  momentum,
  velocityPct,
  recent3m,
  prior3m,
  compact = false,
}: MomentumChipProps) {
  const p = MOMENTUM_PRESENTATION[momentum];
  const lowBase = velocityPct !== null && prior3m < LOW_BASE_THRESHOLD_9L;
  const text = qoqChipText(velocityPct, prior3m, recent3m);
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25, maxWidth: 220 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
            Quarter-over-quarter
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent3m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior3m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Tiny prior baseline — showing absolute change instead of a misleading %.
            </Typography>
          )}
          {velocityPct === null && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-3M activity — this account is brand new or restarting.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          px: compact ? 0.75 : 1,
          py: compact ? 0.2 : 0.3,
          borderRadius: 1,
          bgcolor: p.bg,
          color: p.color,
          fontWeight: 700,
          fontSize: compact ? 10.5 : 11.5,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${p.color}` : 'none',
        }}
      >
        <p.Icon sx={{ fontSize: compact ? 11 : 13 }} />
        {text}
      </Box>
    </Tooltip>
  );
}

interface YoyChipProps {
  velocityPct: number | null;
  recent12m: number;
  prior12m: number;
}

function YoyChip({ velocityPct, recent12m, prior12m }: YoyChipProps) {
  const LOW_BASE_12M = 20;
  const lowBase = velocityPct !== null && prior12m < LOW_BASE_12M;
  const isNew = velocityPct === null;
  const isPositive = velocityPct !== null && velocityPct > 0;
  const isNegative = velocityPct !== null && velocityPct < 0;
  const color = isNew
    ? colors.textMuted
    : lowBase
      ? colors.textSecondary
      : isPositive
        ? colors.success
        : isNegative
          ? colors.error
          : colors.textSecondary;
  const bg = isNew
    ? 'transparent'
    : lowBase
      ? '#f1f5f9'
      : isPositive
        ? `${colors.success}10`
        : isNegative
          ? `${colors.error}10`
          : '#f1f5f9';
  let text: string;
  if (isNew) {
    text = 'YoY n/a';
  } else if (lowBase) {
    const diff = recent12m - prior12m;
    text = `YoY ${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  } else {
    const v = velocityPct!;
    const cap = v > 999 ? '+999%+' : v < -999 ? '−999%+' : `${v > 0 ? '+' : ''}${v.toFixed(0)}%`;
    text = `YoY ${cap}`;
  }
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25, maxWidth: 240 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>Year-over-year</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent12m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior12m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Prior-year baseline was tiny — showing absolute change instead.
            </Typography>
          )}
          {isNew && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-year activity. Too new for a YoY read.
            </Typography>
          )}
          {!isNew && !lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              YoY smooths the seasonal noise QoQ catches.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.65,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${color}` : 'none',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

interface FlagChipProps {
  Icon: typeof TrendingUpIcon;
  label: string;
  color: string;
  bg: string;
  tooltip: string;
}

function FlagChip({ Icon, label, color, bg, tooltip }: FlagChipProps) {
  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          px: 0.55,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          cursor: 'help',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon sx={{ fontSize: 11 }} />
        {label}
      </Box>
    </Tooltip>
  );
}

interface MiniBarProps {
  share: number;
  color: string;
}

function MiniBar({ share, color }: MiniBarProps) {
  const pct = Math.max(0, Math.min(1, share)) * 100;
  return (
    <Box
      sx={{
        position: 'relative',
        height: 6,
        bgcolor: `${color}1a`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          bgcolor: color,
          opacity: 0.85,
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

interface PortfolioTileProps {
  eyebrow: string;
  primary: string;
  primaryColor?: string;
  secondary?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error';
  tooltip?: ReactNode;
}

function PortfolioTile({
  eyebrow,
  primary,
  primaryColor,
  secondary,
  tone = 'neutral',
  tooltip,
}: PortfolioTileProps) {
  const toneAccent: Record<NonNullable<PortfolioTileProps['tone']>, string> = {
    neutral: colors.chartPrimary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
  };
  const body = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRight: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderRight: 'none' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: tooltip ? 'help' : 'default',
        transition: 'background-color 0.12s',
        '&:hover': tooltip ? { bgcolor: '#fff' } : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: primaryColor ?? toneAccent[tone],
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFeatureSettings: '"tnum","lnum"',
        }}
      >
        {primary}
      </Typography>
      {secondary !== undefined && (
        <Box sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.3 }}>{secondary}</Box>
      )}
    </Box>
  );
  if (!tooltip) return body;
  return <Tooltip title={tooltip}>{body}</Tooltip>;
}

// ----------------------------------------------------------------
// Sort / filter
// ----------------------------------------------------------------

type SortKey = 'volume' | 'momentum' | 'yoy' | 'recent_q' | 'days_since';
type FilterKey = 'all' | 'rising' | 'slipping_or_worse' | 'dark';

const SORT_OPTIONS: { key: SortKey; label: string; describe: string }[] = [
  { key: 'volume', label: 'Volume', describe: 'Total 6-pk, largest first' },
  { key: 'momentum', label: 'QoQ momentum', describe: 'Recent 3M vs prior 3M' },
  { key: 'yoy', label: 'YoY momentum', describe: 'Recent 12M vs prior 12M' },
  { key: 'recent_q', label: 'Recent quarter', describe: 'Last 3 months volume' },
  { key: 'days_since', label: 'Days dormant', describe: 'Longest gap since last buy' },
];

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All accounts' },
  { key: 'rising', label: 'Rising only' },
  { key: 'slipping_or_worse', label: 'Slipping / declining' },
  { key: 'dark', label: 'Dark (60d+)' },
];

// Premises channel a top-account row belongs to. Online retailers are
// their own channel (carved out of OFF), matching the Premises Mix +
// Follow-Up Tracker.
type PremisesKey = 'ON' | 'OFF' | 'ONLINE' | 'NA' | 'UNKNOWN';
const PREMISES_ORDER: PremisesKey[] = ['ON', 'OFF', 'ONLINE', 'NA', 'UNKNOWN'];
const PREMISES_CHIP_LABEL: Record<PremisesKey, string> = {
  ON: 'ON',
  OFF: 'OFF',
  ONLINE: 'Online',
  NA: 'NA',
  UNKNOWN: 'Unknown',
};

function premisesBucket(a: AccountPerformanceItem): PremisesKey {
  if (a.is_online_retailer) return 'ONLINE';
  const p = a.premises_type;
  if (p === 'ON' || p === 'OFF' || p === 'NA') return p;
  return 'UNKNOWN';
}

type RankedAccount = AccountPerformanceItem & { rank: number };

// velocity_pct / yoy_pct arrive as whole percents; Excel wants fractions.
const pctToFraction = (v: number | null) => (v === null ? null : v / 100);

function buildTopAccountsExport(
  visible: AccountPerformanceItem[],
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const rows: RankedAccount[] = visible.map((a, i) => ({ ...a, rank: i + 1 }));
  return {
    title: 'Top Accounts',
    filters,
    sheets: [
      excelSheet<RankedAccount>({
        name: 'Accounts',
        columns: [
          { header: 'Rank', value: (r) => r.rank, format: 'int', width: 7 },
          ...accountColumns<RankedAccount>(),
          { header: 'Distributor', value: (r) => r.distributor_code, width: 12 },
          {
            header: 'Premises',
            value: (r) => premisesLabel(r.premises_type, r.is_online_retailer),
          },
          { header: '9L cases', value: (r) => toNum(r.cases_9l), format: 'decimal' },
          { header: 'Physical cases', value: (r) => toNum(r.cases_physical), format: 'decimal' },
          {
            header: '6-packs',
            value: (r) => Math.round(toSixPacks(r.cases_9l)),
            format: 'int',
          },
          { header: 'Share of portfolio', value: (r) => r.pct_of_9l, format: 'percent' },
          { header: 'Momentum', value: (r) => MOMENTUM_PRESENTATION[r.momentum].label, width: 11 },
          {
            header: 'QoQ % change',
            value: (r) => pctToFraction(r.velocity_pct),
            format: 'percent',
          },
          { header: 'Recent 3M 9L', value: (r) => toNum(r.recent_3m_9l), format: 'decimal' },
          { header: 'Prior 3M 9L', value: (r) => toNum(r.prior_3m_9l), format: 'decimal' },
          { header: 'YoY % change', value: (r) => pctToFraction(r.yoy_pct), format: 'percent' },
          { header: 'Recent 12M 9L', value: (r) => toNum(r.recent_12m_9l), format: 'decimal' },
          { header: 'Prior 12M 9L', value: (r) => toNum(r.prior_12m_9l), format: 'decimal' },
          { header: 'Top SKU', value: (r) => r.top_product_name, width: 28 },
          { header: 'Top SKU share', value: (r) => r.top_product_share, format: 'percent' },
          { header: 'Products', value: (r) => r.product_count, format: 'int' },
          {
            header: 'Avg 9L / active month',
            value: (r) => toNum(r.avg_9l_per_active_month),
            format: 'decimal',
          },
          { header: 'Active months (12M)', value: (r) => r.months_active_12m, format: 'int' },
          { header: 'First active', value: (r) => isoDate(r.first_active), format: 'date' },
          { header: 'Last active', value: (r) => isoDate(r.last_active), format: 'date' },
          { header: 'Days since', value: (r) => r.days_since, format: 'int' },
        ],
        rows,
      }),
    ],
  };
}

// ----------------------------------------------------------------
// Main component
// ----------------------------------------------------------------

export function TopAccountsTable({ range, limit = 5000 }: TopAccountsTableProps) {
  const { data, isLoading, error } = useAccountPerformance(range, limit);
  const [sort, setSort] = useState<SortKey>('volume');
  const [filter, setFilter] = useState<FilterKey>('all');
  // Premises-type chip filter (multi-select). Empty = show all.
  const [premisesFilter, setPremisesFilter] = useState<Set<PremisesKey>>(new Set());
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);
  // Search query is matched case-insensitively against the account
  // name. Trimmed so trailing spaces from typing don't kill results.
  const [searchQuery, setSearchQuery] = useState('');
  // State multi-select. Empty set = no filter (show every state).
  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set());
  // Pagination — 10 rows/page is the clean-default we settled on for
  // depletions tables. User can bump to 25/50/100/All from the
  // selector. -1 = "All".
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const accounts = useMemo(() => data?.accounts ?? [], [data?.accounts]);
  const totalAll = Number(data?.total_9l ?? 0);
  const top10Share = data?.top_10_share ?? 0;
  const referenceDate = data?.reference_date ?? null;

  // States present in the dataset — drives the state filter menu.
  // Sorted alphabetically so the rep can scan them quickly.
  const availableStates = useMemo(() => {
    const set = new Set<string>();
    for (const a of accounts) if (a.state_code) set.add(a.state_code);
    return [...set].sort();
  }, [accounts]);

  // Median depth across the top-N for the depth signal line.
  const portfolioMedians = useMemo(() => {
    if (accounts.length === 0) return { depth: 0, share: 0 };
    const median = (vals: number[]) => {
      const sorted = vals.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
        : (sorted[mid] ?? 0);
    };
    return {
      depth: median(accounts.map((a) => Number(a.avg_9l_per_active_month))),
      share: median(accounts.map((a) => a.pct_of_9l)),
    };
  }, [accounts]);

  const summary = useMemo(() => {
    if (accounts.length === 0) return null;
    const sortedByVolume = [...accounts].sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l));
    const lead = sortedByVolume[0] ?? null;
    const withVelocity = accounts.filter((a) => a.velocity_pct !== null);
    const topMover = withVelocity
      .slice()
      .sort((a, b) => (b.velocity_pct ?? -Infinity) - (a.velocity_pct ?? -Infinity))[0];
    const biggestSlipper = withVelocity
      .slice()
      .sort((a, b) => (a.velocity_pct ?? Infinity) - (b.velocity_pct ?? Infinity))[0];
    return {
      lead,
      topMover: topMover && (topMover.velocity_pct ?? 0) > 0 ? topMover : null,
      biggestSlipper:
        biggestSlipper && (biggestSlipper.velocity_pct ?? 0) < 0 ? biggestSlipper : null,
    };
  }, [accounts]);

  // Rows passing every filter EXCEPT premises — the premises chip
  // counts read off this so they reflect the current view.
  const filteredRows = useMemo(() => {
    let rows = accounts.slice();

    // Momentum / dormancy filter pill.
    if (filter === 'rising') {
      rows = rows.filter((a) => a.momentum === 'rising');
    } else if (filter === 'slipping_or_worse') {
      rows = rows.filter((a) => a.momentum === 'slipping' || a.momentum === 'declining');
    } else if (filter === 'dark') {
      rows = rows.filter((a) => (a.days_since ?? 0) > 60);
    }

    // State multi-select.
    if (selectedStates.size > 0) {
      rows = rows.filter((a) => a.state_code !== null && selectedStates.has(a.state_code));
    }

    // Name search. Substring, case-insensitive, run against the
    // account name only — city/state already get their own filter.
    const q = searchQuery.trim().toLowerCase();
    if (q.length > 0) {
      rows = rows.filter((a) => a.name.toLowerCase().includes(q));
    }
    return rows;
  }, [accounts, filter, selectedStates, searchQuery]);

  // Count per premises bucket over the pre-premises rows.
  const premisesCounts = useMemo(() => {
    const counts: Record<PremisesKey, number> = { ON: 0, OFF: 0, ONLINE: 0, NA: 0, UNKNOWN: 0 };
    for (const a of filteredRows) {
      const key = premisesBucket(a);
      counts[key] += 1;
    }
    return counts;
  }, [filteredRows]);

  const visible = useMemo(() => {
    let rows = filteredRows.slice();

    // Premises-type chip filter (multi-select). Online retailers are
    // their own channel, carved out of OFF.
    if (premisesFilter.size > 0) {
      rows = rows.filter((a) => premisesFilter.has(premisesBucket(a)));
    }

    const tieBreak = (a: AccountPerformanceItem, b: AccountPerformanceItem) =>
      Number(b.cases_9l) - Number(a.cases_9l);
    switch (sort) {
      case 'momentum':
        rows.sort((a, b) => {
          const av = a.velocity_pct ?? -Infinity;
          const bv = b.velocity_pct ?? -Infinity;
          return bv - av || tieBreak(a, b);
        });
        break;
      case 'yoy':
        rows.sort((a, b) => {
          const av = a.yoy_pct ?? -Infinity;
          const bv = b.yoy_pct ?? -Infinity;
          return bv - av || tieBreak(a, b);
        });
        break;
      case 'recent_q':
        rows.sort((a, b) => Number(b.recent_3m_9l) - Number(a.recent_3m_9l) || tieBreak(a, b));
        break;
      case 'days_since':
        rows.sort((a, b) => (b.days_since ?? -1) - (a.days_since ?? -1) || tieBreak(a, b));
        break;
      case 'volume':
      default:
        rows.sort(tieBreak);
    }
    return rows;
  }, [filteredRows, premisesFilter, sort]);

  // Reset to page 1 whenever the visible set changes underneath the
  // user — otherwise they can end up looking at "page 5 of 1".
  useEffect(() => {
    setPage(0);
  }, [sort, filter, selectedStates, searchQuery, premisesFilter, rowsPerPage]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return visible;
    const start = page * rowsPerPage;
    return visible.slice(start, start + rowsPerPage);
  }, [visible, page, rowsPerPage]);

  // Every filtered + sorted row, not just the current page.
  useSectionExport(
    () =>
      buildTopAccountsExport(visible, [
        // Only a host-supplied range narrows the rows; the default view spans everything.
        range?.from || range?.to
          ? { label: 'Date range', value: `${range.from ?? 'Earliest'} to ${range.to ?? 'latest'}` }
          : { kind: 'info', label: 'Period', value: 'All available data' },
        {
          label: 'Search (account name)',
          value: searchQuery.trim() ? `"${searchQuery.trim()}"` : '',
        },
        {
          label: 'State',
          value: selectedStates.size > 0 ? [...selectedStates].sort().join(', ') : 'All states',
        },
        {
          label: 'Momentum',
          value: FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All accounts',
        },
        {
          label: 'Premises',
          value:
            premisesFilter.size > 0
              ? PREMISES_ORDER.filter((k) => premisesFilter.has(k))
                  .map((k) => PREMISES_CHIP_LABEL[k])
                  .join(', ')
              : 'All',
        },
        {
          kind: 'sort',
          label: 'Sorted by',
          value: `${SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume'} (high → low)`,
        },
        { kind: 'info', label: 'Data through', value: referenceDate ?? '' },
      ]),
    accounts.length > 0,
  );

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load top accounts: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || accounts.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Top Accounts
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Typography color="text.secondary">No accounts to display.</Typography>
          )}
        </Box>
      </Paper>
    );
  }

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume';
  const filterLabel = FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All accounts';

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <StorefrontIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Top Accounts
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Top {accounts.length} buyers · {fmt6PkNumber(totalAll)} 6-pk ({num.format(totalAll)} 9L)
            portfolio total · Top-10 share {Math.round(top10Share * 100)}%
            {referenceDate && ` · ref ${shortMonth(referenceDate)}`}
          </Typography>
        </Box>
      </Box>

      {summary && (
        <Box
          sx={{
            display: 'flex',
            border: `1px solid ${colors.borderLight}`,
            borderRadius: 1.5,
            mb: 2.5,
            overflow: 'hidden',
            bgcolor: colors.surfaceSubtle,
          }}
        >
          <PortfolioTile
            eyebrow="Lead Account"
            primary={summary.lead?.name ?? '—'}
            secondary={
              summary.lead && (
                <>
                  <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>
                    {fmt6PkNumber(summary.lead.cases_9l)}
                  </strong>{' '}
                  6-pk · {num.format(Number(summary.lead.cases_9l))} 9L ·{' '}
                  {(summary.lead.pct_of_9l * 100).toFixed(2)}% of portfolio
                </>
              )
            }
            tooltip={
              summary.lead ? (
                <TooltipBlock
                  title="Largest single account"
                  items={[
                    {
                      label: summary.lead.name,
                      description: `${fmt6PkNumber(Number(summary.lead.cases_9l))} 6-pk · ${(summary.lead.pct_of_9l * 100).toFixed(2)}% of total portfolio · ${[summary.lead.city, summary.lead.state_code].filter(Boolean).join(', ')}`,
                    },
                    {
                      label: 'Reach',
                      description: `${summary.lead.product_count} SKUs · distributor ${summary.lead.distributor_code ?? '—'}`,
                    },
                  ]}
                  tip="The account you'd defend first. Losing them moves the whole number."
                />
              ) : (
                'No accounts in range.'
              )
            }
          />
          <PortfolioTile
            eyebrow="Top Mover"
            primary={summary.topMover ? summary.topMover.name : 'None gaining'}
            tone="success"
            secondary={
              summary.topMover ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MomentumChip
                    momentum={summary.topMover.momentum}
                    velocityPct={summary.topMover.velocity_pct}
                    recent3m={Number(summary.topMover.recent_3m_9l)}
                    prior3m={Number(summary.topMover.prior_3m_9l)}
                    compact
                  />
                  <span>{fmt6PkNumber(Number(summary.topMover.recent_3m_9l))} 6-pk recent Q</span>
                </Box>
              ) : (
                <span style={{ color: colors.textMuted }}>No account growing QoQ</span>
              )
            }
            tooltip={
              summary.topMover ? (
                <TooltipBlock
                  title="Fastest-growing account"
                  items={[
                    {
                      label: summary.topMover.name,
                      description: `Prior 3M: ${fmt6PkNumber(summary.topMover.prior_3m_9l)} 6-pk → Recent 3M: ${fmt6PkNumber(summary.topMover.recent_3m_9l)} 6-pk · ${[summary.topMover.city, summary.topMover.state_code].filter(Boolean).join(', ')}`,
                    },
                  ]}
                  tip="Lean into this relationship — the curve is bending up."
                />
              ) : (
                'No account is gaining QoQ over the selected range.'
              )
            }
          />
          <PortfolioTile
            eyebrow="Biggest Slipper"
            primary={summary.biggestSlipper ? summary.biggestSlipper.name : 'None slipping'}
            tone="error"
            secondary={
              summary.biggestSlipper ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <MomentumChip
                    momentum={summary.biggestSlipper.momentum}
                    velocityPct={summary.biggestSlipper.velocity_pct}
                    recent3m={Number(summary.biggestSlipper.recent_3m_9l)}
                    prior3m={Number(summary.biggestSlipper.prior_3m_9l)}
                    compact
                  />
                  <span>
                    {fmt6PkNumber(Number(summary.biggestSlipper.recent_3m_9l))} 6-pk recent Q
                  </span>
                </Box>
              ) : (
                <span style={{ color: colors.textMuted }}>No account declining QoQ</span>
              )
            }
            tooltip={
              summary.biggestSlipper ? (
                <TooltipBlock
                  title="Most-declining account"
                  items={[
                    {
                      label: summary.biggestSlipper.name,
                      description: `Prior 3M: ${fmt6PkNumber(summary.biggestSlipper.prior_3m_9l)} 6-pk → Recent 3M: ${fmt6PkNumber(summary.biggestSlipper.recent_3m_9l)} 6-pk · ${[summary.biggestSlipper.city, summary.biggestSlipper.state_code].filter(Boolean).join(', ')}`,
                    },
                  ]}
                  tip="Open the row to see what they used to buy. A call may be overdue."
                />
              ) : (
                'No account is declining QoQ over the selected range.'
              )
            }
          />
          <PortfolioTile
            eyebrow="Concentration"
            primary={`${Math.round(top10Share * 100)}%`}
            tone={top10Share > 0.5 ? 'warning' : 'neutral'}
            secondary={
              <>
                Top-10 share ·{' '}
                <strong style={{ color: top10Share > 0.5 ? colors.warning : colors.textSecondary }}>
                  {top10Share > 0.5 ? 'Narrow' : 'Healthy'}
                </strong>{' '}
                customer base
              </>
            }
            tooltip={
              <TooltipBlock
                title="Customer-base concentration"
                items={[
                  {
                    label: `Top-10 share: ${Math.round(top10Share * 100)}%`,
                    description: 'Share of total 6-pk coming from the ten biggest accounts.',
                  },
                  {
                    label: 'Threshold',
                    description:
                      'Above 50% = narrow base. Strong leaders, but a single account loss is felt across the portfolio.',
                  },
                ]}
              />
            }
          />
        </Box>
      )}

      {/* Toolbar — search + state filter + sort/filter pills. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={`Search ${accounts.length.toLocaleString('en-US')} accounts by name…`}
        />
        <StateMultiSelect
          available={availableStates}
          selected={selectedStates}
          onChange={setSelectedStates}
          open={stateMenuOpen}
          onToggle={() => {
            setStateMenuOpen((o) => !o);
            setSortMenuOpen(false);
            setFilterMenuOpen(false);
          }}
        />
        <ToolbarMenu
          label="Sort"
          value={sortLabel}
          open={sortMenuOpen}
          onToggle={() => {
            setSortMenuOpen((o) => !o);
            setFilterMenuOpen(false);
            setStateMenuOpen(false);
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={sort === opt.key}
              primary={opt.label}
              secondary={opt.describe}
              onClick={() => {
                setSort(opt.key);
                setSortMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <ToolbarMenu
          label="Filter"
          value={filterLabel}
          open={filterMenuOpen}
          onToggle={() => {
            setFilterMenuOpen((o) => !o);
            setSortMenuOpen(false);
            setStateMenuOpen(false);
          }}
        >
          {FILTER_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={filter === opt.key}
              primary={opt.label}
              onClick={() => {
                setFilter(opt.key);
                setFilterMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visible.length.toLocaleString('en-US')} of {accounts.length.toLocaleString('en-US')}{' '}
          accounts
        </Typography>
      </Box>

      {/* Premises-type chip filter — narrow to a channel, same pattern
          as the Follow-Up Tracker. Online retailers are their own chip. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            color: colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mr: 0.5,
          }}
        >
          Premises:
        </Typography>
        {PREMISES_ORDER.map((key) => {
          const active = premisesFilter.has(key);
          const count = premisesCounts[key];
          const style = PREMISES_STYLE[key] ?? {
            label: 'Unknown',
            color: colors.textMuted,
            bg: colors.surfaceSubtle,
            border: colors.borderLight,
          };
          const disabled = count === 0;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => {
                const next = new Set(premisesFilter);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                setPremisesFilter(next);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0.4,
                height: 24,
                px: 1,
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 600,
                cursor: disabled ? 'default' : 'pointer',
                bgcolor: active ? style.bg : 'transparent',
                color: active ? style.color : colors.textSecondary,
                border: `1px solid ${active ? style.border : colors.borderLight}`,
                opacity: disabled ? 0.45 : 1,
                transition: 'all 0.12s',
                '&:hover': disabled
                  ? {}
                  : { bgcolor: active ? `${style.color}26` : colors.surfaceSubtle },
              }}
            >
              <span>{PREMISES_CHIP_LABEL[key]}</span>
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: active ? style.color : colors.textMuted,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {count}
              </Box>
            </Box>
          );
        })}
        {premisesFilter.size > 0 ? (
          <Box
            component="button"
            type="button"
            onClick={() => setPremisesFilter(new Set())}
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: 11.5,
              fontWeight: 600,
              color: colors.textMuted,
              transition: 'color 0.15s',
              '&:hover': { color: colors.chartPrimary },
            }}
          >
            Clear
          </Box>
        ) : null}
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '22px 32px 2fr 152px 1.1fr 1.1fr 1.3fr 1fr',
            gap: 1,
            px: 1.75,
            py: 1,
            bgcolor: colors.surfaceSubtle,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          <Box />
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="Rank"
                bullets={[
                  'Position within the current sort order',
                  'Click any row to open its deep-dive panel',
                ]}
              />
            }
          >
            #
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="Account & status flags"
                items={[
                  {
                    label: 'Tier badge',
                    description: 'Rising / Steady / Slipping / Declining / New from QoQ.',
                  },
                ]}
                bullets={[
                  'VIP — top 10% of accounts by share',
                  'Hot — recent 3M growing > 25%',
                  'Single-SKU — top product ≥ 80% of account',
                  'Dark — > 60 days since last activity',
                ]}
              />
            }
          >
            Account
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="12-month volume trend"
                bullets={[
                  'Bars run oldest → latest, left to right',
                  'Tallest bar = peak month',
                  'Outlined bar = current month',
                  'Rail bar = no depletions that month',
                ]}
                tip="Hover any bar for the exact month + 6-pk volume."
              />
            }
          >
            12-mo Trend
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Volume & share"
                items={[
                  { label: 'Volume', description: 'Total 6-pk over the selected date range.' },
                  {
                    label: 'Share',
                    description: "This account's portion of total portfolio volume.",
                  },
                ]}
                tip="Number is tinted by momentum: green if rising, red if declining."
              />
            }
          >
            Volume · Share
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Location & distributor"
                items={[
                  { label: 'City, State', description: 'Where the outlet operates.' },
                  {
                    label: 'Distributor',
                    description: 'Wholesaler short code from the iDIG feed.',
                  },
                ]}
                tip="Hover any cell for the full address and county."
              />
            }
          >
            Location
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Top SKU"
                items={[
                  {
                    label: 'Top SKU',
                    description:
                      'The product driving the most 6-pk for this account, with its share.',
                  },
                  {
                    label: 'Days since',
                    description: 'Days since the last non-zero depletion.',
                  },
                ]}
                tip="Hover for the full top-3 SKU list."
              />
            }
          >
            Top SKU · Days
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Momentum"
                items={[
                  {
                    label: 'QoQ',
                    description:
                      'Recent 3M vs prior 3M. Falls back to absolute Δ on tiny baselines.',
                  },
                  {
                    label: 'YoY',
                    description: 'Recent 12M vs prior 12M — smooths seasonal noise.',
                  },
                ]}
                tip="When QoQ is red but YoY is flat, the dip is likely seasonal."
              />
            }
          >
            QoQ · YoY
          </ColumnHeader>
        </Box>

        {paginated.map((a, i) => {
          // Rank continues across pages — page 2 starts at 26 with
          // 25 rows/page. "All" mode uses the index directly.
          const rank = rowsPerPage === -1 ? i + 1 : page * rowsPerPage + i + 1;
          return (
            <AccountRow
              key={a.account_id}
              index={rank}
              item={a}
              portfolioMedians={portfolioMedians}
            />
          );
        })}

        {visible.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>
            No accounts match the current filter.
          </Box>
        )}
      </Box>

      <PremiumPagination
        count={visible.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      {/* Reader's guide — decodes the chip vocabulary used in the
          table (momentum, low-base, flags), the share metric, and
          the expand-row drill-down. Collapsed by default. */}
      <SectionExplainer
        rows={[
          {
            sample: <TAMomentumChip />,
            term: 'Momentum chip (Growing · Declining · Steady · Hot · New)',
            desc: (
              <>
                Compares the account's <strong>recent 3 months</strong> vs the{' '}
                <strong>3 months before that</strong>. Sort or filter by momentum to focus on
                accounts that are accelerating, sliding, or holding pace.
              </>
            ),
          },
          {
            sample: <TALowBaseBadge />,
            term: 'LOW BASE marker',
            desc: (
              <>
                Appears next to the momentum chip when the prior 3M was very small (under 5 6-pk). A
                "+200%" jump from 0.5 6-pk isn't the same signal as +200% from 50 — the marker tells
                you to read the absolute change, not the percentage.
              </>
            ),
          },
          {
            sample: <TAFlagChip color={colors.error} text="DARK" />,
            term: 'Flag chips next to the account name',
            desc: (
              <>
                Quick health signals derived from the row data: <strong>Dark</strong> (silent ≥90
                days), <strong>Bleeding</strong> (steep recent drop), <strong>Concentrated</strong>{' '}
                (single SKU), <strong>Hot</strong> (recent surge). Hover for the exact criteria.
              </>
            ),
          },
          {
            sample: <TASharePill />,
            term: 'Share % of portfolio',
            desc: (
              <>
                What fraction of <strong>total 6-pk volume</strong> across all accounts this single
                account carries. The top-10 share total appears in the section header — over ~50%
                means you're heavily dependent on a handful of relationships.
              </>
            ),
          },
          {
            sample: <TADepthMarker />,
            term: 'Depth indicator (avg 6-pk per active month)',
            desc: (
              <>
                Volume divided by months the account was active — separates{' '}
                <strong>steady high-volume buyers</strong> from <strong>burst buyers</strong>.
                Colored vs the portfolio median: green if deeper than median, grey if at or below.
              </>
            ),
          },
          {
            sample: <TAExpandIcon />,
            term: 'Click any row to expand',
            desc: (
              <>
                Opens a deep-dive panel: top SKUs by share, lifecycle (first / last active), peak
                month, the full 12-month sparkline, and location + distributor for outreach. Hide
                button at the top of the panel closes it.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

// ----------------------------------------------------------------
// Sample swatches used inside the Top Accounts SectionExplainer
// rows. Each mirrors the actual chip / marker in the table so the
// guide visually maps back to the row content.
// ----------------------------------------------------------------

function TAMomentumChip() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color: colors.success,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
        px: 0.6,
        py: 0.2,
        borderRadius: 0.75,
        whiteSpace: 'nowrap',
      }}
    >
      Growing
    </Box>
  );
}

function TALowBaseBadge() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 8.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: colors.textMuted,
        bgcolor: colors.surfaceHover,
        border: `1px dashed ${colors.border}`,
        px: 0.5,
        py: 0.1,
        borderRadius: 0.5,
        whiteSpace: 'nowrap',
      }}
    >
      LOW BASE
    </Box>
  );
}

function TAFlagChip({ color, text }: { color: string; text: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        color,
        bgcolor: `${color}14`,
        border: `1px solid ${color}40`,
        px: 0.5,
        py: 0.15,
        borderRadius: 0.75,
      }}
    >
      {text}
    </Box>
  );
}

function TASharePill() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: colors.textSecondary,
        fontFeatureSettings: '"tnum"',
        bgcolor: colors.surfaceHover,
        px: 0.6,
        py: 0.15,
        borderRadius: 0.75,
      }}
    >
      4.2%
    </Box>
  );
}

function TADepthMarker() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
      <Box
        sx={{
          width: 4,
          height: 14,
          bgcolor: colors.success,
          borderRadius: 0.5,
        }}
      />
      <Box
        component="span"
        sx={{ fontSize: 10, color: colors.textSecondary, fontFeatureSettings: '"tnum"' }}
      >
        24 6-pk/mo
      </Box>
    </Box>
  );
}

function TAExpandIcon() {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 14,
        color: colors.chartPrimary,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      ⌄
    </Box>
  );
}

// ----------------------------------------------------------------
// Row + deep-dive panel
// ----------------------------------------------------------------

const FLAG_VIP_SHARE = 0.01; // ≥1% of total portfolio = VIP (top of the list)
const FLAG_SINGLE_SKU_THRESHOLD = 0.8; // top SKU ≥ 80% of account = concentration risk
const FLAG_DARK_DAYS = 60;

interface AccountRowProps {
  index: number;
  item: AccountPerformanceItem;
  portfolioMedians: { depth: number; share: number };
}

function AccountRow({ index, item, portfolioMedians }: AccountRowProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const [expanded, setExpanded] = useState(false);

  const volume = Number(item.cases_9l);
  const physical = Number(item.cases_physical);
  const avgPerActiveMonth = Number(item.avg_9l_per_active_month);
  const sharePct = item.pct_of_9l * 100;
  const topProdSharePct = item.top_product_share * 100;
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);

  const flags: Array<{
    key: string;
    Icon: typeof TrendingUpIcon;
    label: string;
    color: string;
    bg: string;
    tooltip: string;
  }> = [];
  if (item.pct_of_9l >= FLAG_VIP_SHARE) {
    flags.push({
      key: 'vip',
      Icon: WorkspacePremiumIcon,
      label: 'VIP',
      color: colors.gold,
      bg: `${colors.gold}1f`,
      tooltip: `${(item.pct_of_9l * 100).toFixed(2)}% of the entire portfolio volume comes from this single account. Treat as VIP — direct relationship, no surprises.`,
    });
  }
  if (item.momentum === 'rising') {
    flags.push({
      key: 'hot',
      Icon: LocalFireDepartmentIcon,
      label: 'Hot',
      color: colors.success,
      bg: `${colors.success}14`,
      tooltip: `Rising — QoQ ${qoqChipText(item.velocity_pct, prior3m, recent3m)} vs prior 3 months.`,
    });
  }
  if (item.top_product_share >= FLAG_SINGLE_SKU_THRESHOLD && item.top_product_name) {
    flags.push({
      key: 'singlesku',
      Icon: WineBarIcon,
      label: 'Single-SKU',
      color: colors.info,
      bg: `${colors.info}14`,
      tooltip: `${shortSkuName(item.top_product_name)} = ${topProdSharePct.toFixed(0)}% of this account. Cross-sell more SKUs to harden the relationship.`,
    });
  }
  if ((item.days_since ?? 0) > FLAG_DARK_DAYS) {
    flags.push({
      key: 'dark',
      Icon: NightsStayIcon,
      label: 'Dark',
      color: colors.error,
      bg: `${colors.error}14`,
      tooltip: `No depletion activity for ${item.days_since} days. Last active ${
        item.last_active ? shortMonth(item.last_active) : '—'
      }. A call may be overdue.`,
    });
  }
  if (item.product_count === 1 && item.momentum !== 'new') {
    flags.push({
      key: 'mono',
      Icon: WarningAmberIcon,
      label: 'Mono-SKU',
      color: colors.warning,
      bg: `${colors.warning}14`,
      tooltip:
        'This account has only ever bought a single SKU. High upside if you can land a second.',
    });
  }

  // Depth signal vs portfolio median
  const depthPctVsMedian =
    portfolioMedians.depth > 0
      ? ((avgPerActiveMonth - portfolioMedians.depth) / portfolioMedians.depth) * 100
      : 0;
  const depthSignal: { label: string; color: string } = (() => {
    if (avgPerActiveMonth === 0) return { label: 'no depth', color: colors.textMuted };
    if (depthPctVsMedian >= 25) return { label: 'deeper than median', color: colors.success };
    if (depthPctVsMedian <= -25) return { label: 'thinner than median', color: colors.warning };
    return { label: 'around median', color: colors.textMuted };
  })();

  const volumeColor =
    item.momentum === 'rising'
      ? colors.success
      : item.momentum === 'declining'
        ? colors.error
        : colors.textPrimary;
  const rowTint =
    item.momentum === 'rising'
      ? `${colors.success}06`
      : item.momentum === 'declining'
        ? `${colors.error}06`
        : 'transparent';

  return (
    <Box
      sx={{
        bgcolor: expanded ? colors.surfaceSubtle : rowTint,
        borderBottom: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((x) => !x);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: '22px 32px 2fr 152px 1.1fr 1.1fr 1.3fr 1fr',
          gap: 1,
          px: 1.75,
          py: 1.25,
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'background-color 0.12s',
          '&:hover': { bgcolor: colors.surfaceHover },
          '&:focus-visible': {
            outline: `2px solid ${colors.chartPrimary}`,
            outlineOffset: -2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRightIcon
            sx={{
              fontSize: 16,
              color: colors.textMuted,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s ease',
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 3,
              alignSelf: 'stretch',
              bgcolor: presentation.color,
              borderRadius: 1,
              opacity: 0.7,
              mr: -0.25,
            }}
          />
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: colors.textMuted,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {index}
          </Typography>
        </Box>

        {/* Account name + badge + flags */}
        <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip
            title={
              <Box sx={{ p: 0.5, maxWidth: 280 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{item.name}</Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    gap: '4px 12px',
                    fontSize: 11,
                  }}
                >
                  <span style={{ opacity: 0.7 }}>City / State</span>
                  <span style={{ fontWeight: 600 }}>
                    {[item.city, item.state_code].filter(Boolean).join(', ') || '—'}
                  </span>
                  <span style={{ opacity: 0.7 }}>County</span>
                  <span style={{ fontWeight: 600 }}>{item.county ?? '—'}</span>
                  <span style={{ opacity: 0.7 }}>ZIP</span>
                  <span style={{ fontWeight: 600 }}>{item.zip_code ?? '—'}</span>
                  <span style={{ opacity: 0.7 }}>Distributor</span>
                  <span style={{ fontWeight: 600 }}>{item.distributor_code ?? '—'}</span>
                  <span style={{ opacity: 0.7 }}>SKUs purchased</span>
                  <span style={{ fontWeight: 600 }}>{item.product_count}</span>
                  <span style={{ opacity: 0.7 }}>First active</span>
                  <span style={{ fontWeight: 600 }}>
                    {item.first_active ? shortMonth(item.first_active) : '—'}
                  </span>
                  <span style={{ opacity: 0.7 }}>Last active</span>
                  <span style={{ fontWeight: 600 }}>
                    {item.last_active ? shortMonth(item.last_active) : '—'}
                  </span>
                </Box>
                <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
                  Click row for full deep-dive
                </Typography>
              </Box>
            }
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'help',
                borderBottom: `1px dotted ${colors.borderLight}`,
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {item.name}
            </Typography>
          </Tooltip>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
            <PremisesBadge premises={item.premises_type} online={item.is_online_retailer} />
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                color: presentation.color,
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                bgcolor: presentation.bg,
              }}
            >
              {presentation.label}
            </Box>
            {flags.map((f) => (
              <FlagChip
                key={f.key}
                Icon={f.Icon}
                label={f.label}
                color={f.color}
                bg={f.bg}
                tooltip={f.tooltip}
              />
            ))}
          </Box>
        </Box>

        {/* Sparkline */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                12-month volume
              </Typography>
              {item.monthly_series.map((s) => (
                <Box
                  key={s.period}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  <span style={{ opacity: 0.7 }}>{shortMonth(s.period)}</span>
                  <span style={{ fontWeight: 600 }}>{fmt6PkNumber(s.cases_9l)} 6-pk</span>
                </Box>
              ))}
            </Box>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
            <Sparkline series={item.monthly_series} color={presentation.color} />
          </Box>
        </Tooltip>

        {/* Volume + share */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                Volume breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
                <span style={{ opacity: 0.7 }}>6-pack equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>9L equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {num.format(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>Physical cases</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {physical > 0 ? num.format(physical) : '—'}
                </span>
                <span style={{ opacity: 0.7 }}>Share of portfolio</span>
                <span style={{ fontWeight: 600 }}>{sharePct.toFixed(2)}%</span>
                <span style={{ opacity: 0.7 }}>Avg / active month</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(avgPerActiveMonth)} 6-pk
                </span>
                <span style={{ opacity: 0.7 }}>Portfolio median depth</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(portfolioMedians.depth)} 6-pk
                </span>
              </Box>
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: volumeColor,
                lineHeight: 1,
                fontFeatureSettings: '"tnum","lnum"',
              }}
            >
              {fmt6PkNumber(volume)}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.25 }}>
              6-pk · {num.format(volume)} 9L · {sharePct.toFixed(2)}% share
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                color: depthSignal.color,
                mt: 0.2,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {num.format(avgPerActiveMonth)}/mo · {depthSignal.label}
            </Typography>
          </Box>
        </Tooltip>

        {/* Location + distributor */}
        {/* Location cell — visible: city / state + distributor.
            Hover reveals the full mailing address (street, city,
            state ZIP, county) the rep needs to plan a call or drop
            into a map. Mirrors the Follow-Up Tracker pattern. */}
        <Tooltip
          title={
            <Box sx={{ p: 0.25, minWidth: 200, maxWidth: 280 }}>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  mb: 0.5,
                }}
              >
                Address
              </Typography>
              {item.address ? (
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary, mb: 0.25 }}
                >
                  {item.address}
                </Typography>
              ) : (
                <Typography
                  sx={{ fontSize: 11.5, color: colors.textMuted, fontStyle: 'italic', mb: 0.25 }}
                >
                  No street address on file
                </Typography>
              )}
              {(item.city || item.state_code || item.zip_code) && (
                <Typography sx={{ fontSize: 11.5, color: colors.textSecondary }}>
                  {[[item.city, item.state_code].filter(Boolean).join(', '), item.zip_code]
                    .filter(Boolean)
                    .join(' ')}
                </Typography>
              )}
              {item.county && (
                <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.25 }}>
                  {item.county} County
                </Typography>
              )}
              {item.distributor_code && (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.textMuted,
                    mt: 0.5,
                    pt: 0.5,
                    borderTop: `1px solid ${colors.borderLight}`,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  Distributor · <strong>{item.distributor_code}</strong>
                </Typography>
              )}
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', minWidth: 0, cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 600,
                color: colors.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                borderBottom: `1px dotted ${colors.borderLight}`,
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {[item.city, item.state_code].filter(Boolean).join(', ') || '—'}
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: colors.textMuted,
                mt: 0.25,
                fontFeatureSettings: '"tnum"',
              }}
            >
              Dist {item.distributor_code ?? '—'}
            </Typography>
          </Box>
        </Tooltip>

        {/* Top SKU + days since */}
        <Tooltip
          title={
            <Box sx={{ p: 0.5, fontSize: 11, minWidth: 220 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.75 }}>
                Top SKUs for this account
              </Typography>
              {item.top_products.length === 0 ? (
                <Typography sx={{ fontSize: 11, opacity: 0.7 }}>No product data</Typography>
              ) : (
                item.top_products.map((p) => (
                  <Box
                    key={p.product_id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 0.75,
                      alignItems: 'baseline',
                      mb: 0.4,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{shortSkuName(p.product_name)}</span>
                    <span style={{ fontWeight: 700, color: colors.textSecondary }}>
                      {(p.share * 100).toFixed(0)}%
                    </span>
                  </Box>
                ))
              )}
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help', minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {item.top_product_name ? shortSkuName(item.top_product_name) : '—'}
              <Box
                component="span"
                sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5, fontSize: 11 }}
              >
                {topProdSharePct.toFixed(0)}%
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: (item.days_since ?? 0) > FLAG_DARK_DAYS ? colors.error : colors.textMuted,
                mt: 0.25,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {item.days_since !== null ? `${item.days_since}d since last buy` : 'No activity'}
            </Typography>
          </Box>
        </Tooltip>

        {/* QoQ + YoY */}
        <Box
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4 }}
          onClick={(e) => e.stopPropagation()}
        >
          <MomentumChip
            momentum={item.momentum}
            velocityPct={item.velocity_pct}
            recent3m={recent3m}
            prior3m={prior3m}
          />
          <YoyChip
            velocityPct={item.yoy_pct}
            recent12m={Number(item.recent_12m_9l)}
            prior12m={Number(item.prior_12m_9l)}
          />
        </Box>
      </Box>

      {expanded && <ExpandedDeepDive item={item} portfolioMedians={portfolioMedians} />}
    </Box>
  );
}

// ----------------------------------------------------------------
// Expand panel
// ----------------------------------------------------------------

interface ExpandedDeepDiveProps {
  item: AccountPerformanceItem;
  portfolioMedians: { depth: number; share: number };
}

function ExpandedDeepDive({ item, portfolioMedians }: ExpandedDeepDiveProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);
  const maxBaseline = Math.max(recent3m, prior3m, 1);
  const physical = Number(item.cases_physical);
  const volume = Number(item.cases_9l);
  const avgPerActiveMonth = Number(item.avg_9l_per_active_month);
  const recent12m = Number(item.recent_12m_9l);
  const prior12m = Number(item.prior_12m_9l);
  const peakMonthLabel = item.peak_month ? shortMonth(item.peak_month) : null;
  const peakMonthName = item.peak_month
    ? (['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
        Number(item.peak_month.split('-')[1]) - 1
      ] ?? null)
    : null;
  const rhythmLabel =
    item.months_active_12m === 12
      ? 'Active every month'
      : item.months_active_12m >= 9
        ? 'Mostly active'
        : item.months_active_12m >= 5
          ? 'Sporadic'
          : 'Rare';

  const sectionTitle = (text: string) => (
    <Typography
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: colors.textMuted,
        mb: 1,
      }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: `1px dashed ${colors.borderLight}`,
        bgcolor: colors.surfaceSubtle,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.2fr 1.4fr' },
        gap: 2.5,
      }}
    >
      {/* Top SKUs */}
      <Box>
        {sectionTitle('Top SKUs bought')}
        {item.top_products.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>No SKU data.</Typography>
        ) : (
          item.top_products.map((p) => (
            <Box key={p.product_id} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={p.product_name}
                >
                  {shortSkuName(p.product_name)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(Number(p.cases_9l))} 6-pk
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(p.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={p.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Recent vs Prior (3M + 12M) */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          {sectionTitle('Recent vs Prior 3M')}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Recent
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={recent3m / maxBaseline} color={presentation.color} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(recent3m)} 6-pk
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Prior
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={prior3m / maxBaseline} color={colors.textMuted} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(prior3m)} 6-pk
              </Box>
            </Box>
          </Box>
        </Box>

        <Box>
          {sectionTitle('Recent vs Prior 12M (YoY)')}
          {(() => {
            const maxYoy = Math.max(recent12m, prior12m, 1);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Recent
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={recent12m / maxYoy} color={presentation.color} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(recent12m)} 6-pk
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Prior
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={prior12m / maxYoy} color={colors.textMuted} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textSecondary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(prior12m)} 6-pk
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* Account facts */}
      <Box>
        {sectionTitle('Account facts')}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: '4px 12px',
            fontSize: 11.5,
            alignItems: 'baseline',
          }}
        >
          <span style={{ color: colors.textMuted }}>Location</span>
          <span style={{ fontWeight: 600 }}>
            {[item.city, item.state_code].filter(Boolean).join(', ') || '—'}
            {item.zip_code && (
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · {item.zip_code}
              </Box>
            )}
          </span>
          <span style={{ color: colors.textMuted }}>County</span>
          <span style={{ fontWeight: 600 }}>{item.county ?? '—'}</span>
          <span style={{ color: colors.textMuted }}>Distributor</span>
          <span style={{ fontWeight: 600 }}>{item.distributor_code ?? '—'}</span>
          <span style={{ color: colors.textMuted }}>Active rhythm</span>
          <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
            {item.months_active_12m} / 12 months
            <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
              · {rhythmLabel}
            </Box>
          </span>
          {peakMonthName && (
            <>
              <span style={{ color: colors.textMuted }}>Peak month</span>
              <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                {peakMonthLabel}
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  · {fmt6PkNumber(Number(item.peak_month_9l))} 6-pk · Peaks in {peakMonthName}
                </Box>
              </span>
            </>
          )}
          <span style={{ color: colors.textMuted }}>Total volume</span>
          <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
            {fmt6PkNumber(volume)} 6-pk
            {physical > 0 && (
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · {num.format(physical)} physical
              </Box>
            )}
          </span>
          <span style={{ color: colors.textMuted }}>Avg / active month</span>
          <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
            {fmt6PkNumber(avgPerActiveMonth)} 6-pk
            <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
              · median {num.format(portfolioMedians.depth)}
            </Box>
          </span>
          <span style={{ color: colors.textMuted }}>SKUs purchased</span>
          <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
            {item.product_count}
          </span>
          <span style={{ color: colors.textMuted }}>Lifecycle</span>
          <span style={{ fontWeight: 600 }}>
            {item.first_active ? shortMonth(item.first_active) : '—'} →{' '}
            {item.last_active ? shortMonth(item.last_active) : '—'}
            {item.days_since !== null && (
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · {item.days_since}d ago
              </Box>
            )}
          </span>
        </Box>
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------
// Toolbar menu
// ----------------------------------------------------------------

interface ToolbarMenuProps {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function ToolbarMenu({ label, value, open, onToggle, children }: ToolbarMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Close on outside click. While open, `onToggle` is effectively a
  // "close" action — clicking the toggle button itself is inside the
  // wrapper ref so the handler is short-circuited there.
  useClickOutside(wrapperRef, onToggle, open);
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open ? colors.chartPrimary : colors.border}`,
          bgcolor: open ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          transition: 'border-color 0.12s, background-color 0.12s',
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          {label}:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {value}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 240,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            py: 0.5,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface MenuRowProps {
  selected: boolean;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

function MenuRow({ selected, primary, secondary, onClick }: MenuRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        px: 1.5,
        py: 0.9,
        textAlign: 'left',
        bgcolor: selected ? `${colors.chartPrimary}10` : 'transparent',
        '&:hover': { bgcolor: selected ? `${colors.chartPrimary}18` : colors.surfaceHover },
      }}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: selected ? 700 : 500,
          color: selected ? colors.chartPrimary : colors.textPrimary,
          lineHeight: 1.3,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.15 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// Search input — name match. Compact, premium, with a clear button.
// ----------------------------------------------------------------

interface SearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

function SearchInput({ value, onChange, placeholder }: SearchInputProps) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.4,
        borderRadius: 1,
        border: `1px solid ${value ? colors.chartPrimary : colors.border}`,
        bgcolor: '#fff',
        minWidth: 260,
        transition: 'border-color 0.12s',
        '&:hover': { borderColor: colors.chartPrimary },
        '&:focus-within': {
          borderColor: colors.chartPrimary,
          boxShadow: `0 0 0 3px ${colors.chartPrimary}1a`,
        },
      }}
    >
      <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
      <InputBase
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          flex: 1,
          fontSize: 12,
          color: colors.textPrimary,
          '& input::placeholder': { color: colors.textMuted, opacity: 1, fontSize: 11.5 },
        }}
      />
      {value && (
        <Box
          component="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            color: colors.textMuted,
            '&:hover': { bgcolor: colors.surfaceHover, color: colors.textPrimary },
          }}
        >
          <ClearIcon sx={{ fontSize: 13 }} />
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// State multi-select — chip toggles inside a popover.
// ----------------------------------------------------------------

interface StateMultiSelectProps {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  open: boolean;
  onToggle: () => void;
}

function StateMultiSelect({
  available,
  selected,
  onChange,
  open,
  onToggle,
}: StateMultiSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  const valueLabel =
    selected.size === 0
      ? 'All states'
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} states`;

  const toggle = (code: string) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(next);
  };

  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open || selected.size > 0 ? colors.chartPrimary : colors.border}`,
          bgcolor: open || selected.size > 0 ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          transition: 'border-color 0.12s, background-color 0.12s',
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          State:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {valueLabel}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 280,
            maxWidth: 360,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
            p: 1.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: colors.textMuted }}>
              {available.length} states
            </Typography>
            {selected.size > 0 && (
              <Box
                component="button"
                onClick={() => onChange(new Set())}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.chartPrimary,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Clear all
              </Box>
            )}
          </Box>
          <Box
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 240, overflow: 'auto' }}
          >
            {available.map((code) => {
              const isSel = selected.has(code);
              return (
                <Box
                  key={code}
                  component="button"
                  onClick={() => toggle(code)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    borderRadius: 0.75,
                    border: `1px solid ${isSel ? colors.chartPrimary : colors.border}`,
                    bgcolor: isSel ? colors.chartPrimary : '#fff',
                    color: isSel ? '#fff' : colors.textPrimary,
                    fontSize: 11.5,
                    fontWeight: isSel ? 700 : 500,
                    letterSpacing: 0.3,
                    fontFeatureSettings: '"tnum"',
                    transition: 'all 0.12s',
                    '&:hover': {
                      borderColor: colors.chartPrimary,
                      bgcolor: isSel ? colors.chartPrimary : `${colors.chartPrimary}10`,
                    },
                  }}
                >
                  {code}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
